import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { ApiMeta } from '../../../_core/_model/api-response';
import {
  DepositRiskOperationalSummaryDto,
  DepositRiskReviewDetailsDto,
  DepositRiskReviewListItemDto,
  MlOperationalAlertDto,
  ResolveDepositRiskReviewRequest
} from '../../../_core/_model/machine-learning';
import { MachineLearningAdminService } from '../../../_core/_services/machine-learning-admin.service';
import { NotificationService } from '../../../_core/_services/notification.service';

@Component({
  selector: 'app-admin-ml-deposit-risk',
  standalone: false,
  templateUrl: './admin-ml-deposit-risk.html',
  styleUrl: './admin-ml-deposit-risk.css'
})
export class AdminMlDepositRisk implements OnInit {
  loading = false;
  actionLoading = false;
  error = '';
  summary: DepositRiskOperationalSummaryDto | null = null;
  alerts: MlOperationalAlertDto[] = [];
  reviews: DepositRiskReviewListItemDto[] = [];
  meta: ApiMeta | null = null;
  details: DepositRiskReviewDetailsDto | null = null;
  resolutions: string[] = [];
  selected: DepositRiskReviewListItemDto | null = null;
  action = '';
  notes = '';
  resolution = '';
  outcome = 'ConfirmedNormal';
  replaceExistingOutcome = false;
  filterStatus = '';

  constructor(
    private mlService: MachineLearningAdminService,
    private notify: NotificationService
  ) {}

  ngOnInit(): void { this.load(1); }

  load(page = 1): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      summary: this.mlService.getDepositRiskSummary(),
      alerts: this.mlService.getDepositRiskAlerts(),
      reviews: this.mlService.getDepositRiskReviews({ status: this.filterStatus || null, page, rowsPerPage: 20 }),
      resolutions: this.mlService.getDepositRiskResolutions()
    }).subscribe({
      next: result => {
        this.summary = result.summary.data;
        this.alerts = result.alerts.data || [];
        this.reviews = result.reviews.data || [];
        this.meta = result.reviews.meta;
        this.resolutions = result.resolutions.data || [];
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = this.errorMessage(error, 'Unable to load deposit-risk reviews.');
        this.loading = false;
      }
    });
  }

  view(review: DepositRiskReviewListItemDto): void {
    this.mlService.getDepositRiskReview(review.id).subscribe({
      next: response => this.details = response.data,
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to load review details.'), 'Error')
    });
  }

  openAction(review: DepositRiskReviewListItemDto, action: string): void {
    this.selected = review;
    this.action = action;
    this.notes = '';
    this.resolution = this.resolutions[0] || '';
    this.outcome = action === 'resolve' && review.classification.toLowerCase() === 'critical' ? 'ConfirmedSuspicious' : 'ConfirmedNormal';
    this.replaceExistingOutcome = false;
  }

  closeAction(): void { this.selected = null; this.action = ''; }

  submitAction(): void {
    if (!this.selected) return;
    const id = this.selected.id;
    this.actionLoading = true;
    const request = { notes: this.notes || null };
    const operation = this.action === 'start'
      ? this.mlService.startDepositRiskReview(id, request)
      : this.action === 'source'
        ? this.mlService.requestDepositSourceOfFunds(id, request)
        : this.action === 'escalate'
          ? this.mlService.escalateDepositRiskReview(id, request)
          : this.mlService.resolveDepositRiskReview(id, {
              resolution: this.resolution,
              outcome: this.outcome,
              notes: this.notes || null,
              replaceExistingOutcome: this.replaceExistingOutcome
            } as ResolveDepositRiskReviewRequest);

    operation.subscribe({
      next: response => {
        this.notify.showSuccess(response.message || 'Deposit-risk review updated.', 'Success');
        this.closeAction();
        this.details = null;
        this.load(this.meta?.currentPage || 1);
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to update the deposit-risk review.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  canStart(item: DepositRiskReviewListItemDto): boolean { return item.status.toLowerCase() === 'pending'; }
  canRequestSource(item: DepositRiskReviewListItemDto): boolean { return ['pending', 'inreview'].includes(item.status.toLowerCase()); }
  canEscalate(item: DepositRiskReviewListItemDto): boolean { return !['resolved', 'escalated'].includes(item.status.toLowerCase()); }
  canResolve(item: DepositRiskReviewListItemDto): boolean { return item.status.toLowerCase() !== 'resolved'; }

  badgeClass(value?: string | null): string {
    switch ((value || '').toLowerCase()) {
      case 'resolved': case 'low': return 'text-bg-success';
      case 'pending': case 'moderate': return 'text-bg-warning text-dark';
      case 'inreview': case 'sourceoffundsrequested': return 'text-bg-primary';
      case 'escalated': case 'critical': return 'text-bg-danger';
      case 'high': return 'text-bg-warning text-dark';
      default: return 'text-bg-secondary';
    }
  }

  alertClass(severity?: string | null): string {
    return (severity || '').toLowerCase() === 'critical' ? 'alert-danger' : (severity || '').toLowerCase() === 'high' ? 'alert-warning' : 'alert-info';
  }

  actionTitle(): string {
    switch (this.action) {
      case 'start': return 'Start review';
      case 'source': return 'Request source-of-funds verification';
      case 'escalate': return 'Escalate review';
      case 'resolve': return 'Resolve review and label prediction';
      default: return 'Review action';
    }
  }

  private errorMessage(error: any, fallback: string): string {
    return error?.error?.error?.details || error?.error?.message || error?.message || fallback;
  }
}
